import moment from 'moment-timezone';

const STORAGE_KEY = 'msptrw_time_tracker_tz';

export function loadStoredTimezone() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v == null || v === '') return '';
    if (!moment.tz.zone(v)) return '';
    return v;
  } catch (e) {
    return '';
  }
}

/** @param {string} iana */
export function saveStoredTimezone(iana) {
  try {
    if (iana == null || iana === '') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, iana);
  } catch (e) {
    /* ignore */
  }
}

/**
 * Empty stored value → always follow the browser’s current zone (e.g. travel / DST).
 * Otherwise use the saved IANA id if valid.
 */
export function getEffectiveTimezone(stored) {
  if (stored && moment.tz.zone(stored)) return stored;
  return moment.tz.guess();
}

/**
 * Monday 00:01 in `timeZone`, week interval [start, start + 7 days).
 * Same rules as the previous local-only helper, but in the given zone.
 */
export function getWeekStartMoment(input, timeZone) {
  const tz = timeZone || moment.tz.guess();
  const m = moment.tz(input, tz);
  if (!m.isValid()) return moment.tz(tz);
  const dow = m.day();
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  const cal = m.clone().startOf('day').subtract(daysFromMonday, 'days');
  let weekStart = cal.clone().hour(0).minute(1).second(0).millisecond(0);
  if (m.isBefore(weekStart)) weekStart.subtract(7, 'days');
  return weekStart;
}

export function isTodayInZone(date, timeZone) {
  const tz = timeZone || moment.tz.guess();
  const d = moment.tz(date, tz);
  const now = moment.tz(tz);
  return d.format('YYYY-MM-DD') === now.format('YYYY-MM-DD');
}

/**
 * Human-friendly label for a week block.
 * - `title` — short name: "This week", "Last week", or a dated label for older weeks.
 * - `range` — full weekday range (Mon → Sun) in plain English.
 */
export function formatWeekHeading(weekStartMoment, currentWeekStartMoment) {
  const deltaWeeks = Math.round(
    (currentWeekStartMoment.valueOf() - weekStartMoment.valueOf()) /
      (7 * 24 * 60 * 60 * 1000),
  );
  const start = weekStartMoment;
  const end = weekStartMoment.clone().add(6, 'days');
  const sameYear = start.format('YYYY') === end.format('YYYY');
  const startLabel = sameYear
    ? start.format('dddd MMM D')
    : start.format('dddd MMM D, YYYY');
  const endLabel = end.format('dddd MMM D, YYYY');
  const range = `${startLabel} – ${endLabel}`;
  let title;
  if (deltaWeeks === 0) title = 'This week';
  else if (deltaWeeks === 1) title = 'Last week';
  else title = `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  return { title, range };
}

let cachedZoneList = null;

const COMMON_US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Phoenix', label: 'Arizona — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
];

/** @returns {{ value: string, label: string }[]} */
export function getIanaTimezoneOptions() {
  if (cachedZoneList) return cachedZoneList;
  let ids =
    typeof Intl !== 'undefined' &&
    Intl.supportedValuesOf &&
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [
          'UTC',
          'America/New_York',
          'America/Chicago',
          'America/Denver',
          'America/Phoenix',
          'America/Los_Angeles',
          'America/Anchorage',
          'Pacific/Honolulu',
          'Europe/London',
          'Europe/Paris',
          'Asia/Dubai',
          'Asia/Kolkata',
          'Asia/Tokyo',
          'Australia/Sydney',
        ];
  ids = [...ids].sort((a, b) => a.localeCompare(b));
  cachedZoneList = ids.map((iana) => ({
    value: iana,
    label: iana.replace(/_/g, ' '),
  }));
  return cachedZoneList;
}

/**
 * Schedule pickers show common US zones until the user searches.
 * A saved zone that is not in that short list stays visible.
 * @returns {{ value: string, label: string }[]}
 */
export function getTimezoneSelectOptions(search, pinned) {
  const all = getIanaTimezoneOptions();
  const query = (search || '').trim().toLowerCase();
  let options;
  if (!query) {
    const known = new Set(all.map((zone) => zone.value));
    options = COMMON_US_TIMEZONES.filter(
      (zone) => known.has(zone.value) || moment.tz.zone(zone.value),
    );
  } else {
    options = all
      .filter(
        (zone) =>
          zone.label.toLowerCase().includes(query) ||
          zone.value.toLowerCase().includes(query),
      )
      .slice(0, 40);
  }
  if (pinned && !options.some((zone) => zone.value === pinned)) {
    const found = all.find((zone) => zone.value === pinned);
    options = [
      found || { value: pinned, label: String(pinned).replace(/_/g, ' ') },
      ...options,
    ];
  }
  return options;
}

/** One-line summary for the tracker header (zone + abbrev + current clock there). */
export function getTrackerTimezoneSummary(timeZone) {
  const tz = timeZone || moment.tz.guess();
  const m = moment.tz(moment(), tz);
  return `${tz} · ${m.format('z')} · ${m.format('ddd, MMM D, h:mm A')}`;
}
